import { mergeClassNames } from '../../../utils/mergeClassNames';
import { useEffect } from 'react';
import { ListBoxItem, type ListBoxItemProps } from 'react-aria-components';
import type { Key } from './Select';

import { Icon, IconName } from '@zapier/zinnia-icons';

import { useSelectContext } from './SelectBase/SelectContext';
import styles from './SelectItem.module.scss';

export type SelectItemProps = Omit<ListBoxItemProps, 'children' | 'value'> & {
  iconName?: IconName;
  /** The size of the Item */
  size?: 'medium' | 'large';
  /** Value passed to `Select`'s `onChange` callback when the item is selected. */
  value: Key;
} & (
    | {
        children: string;
        textValue?: never;
      }
    | {
        children: ListBoxItemProps['children'];
        /** A text representation of the value.
         * Not necessary when `children` is a simple string.  */
        textValue: string;
      }
  );

/**
 * Component containting the Select items
 */
export const SelectItem = ({
  children,
  className,
  isDisabled = false,
  iconName,
  size,
  textValue,
  value,
  ...props
}: SelectItemProps) => {
  const { registerDisabledItem, size: sizeFromContext } = useSelectContext();

  useEffect(() => {
    if (isDisabled) {
      registerDisabledItem(value || '');
    }
  }, [isDisabled, value, registerDisabledItem]);

  // ListBoxItem below will never have plain-text children, so we always need to pass it a textValue to prevent a react-aria warning.
  const getTextValue = () => {
    if (typeof children === 'string') {
      return children;
    }

    if (textValue) {
      return textValue;
    }

    return undefined;
  };

  return (
    <ListBoxItem
      className={(renderProps) =>
        mergeClassNames([styles.selectItem, className], renderProps)
      }
      data-size={size ?? sizeFromContext}
      data-zds
      id={value}
      textValue={getTextValue()}
      {...props}
    >
      {(renderProps) => (
        <>
          {iconName && <Icon name={iconName} size={16} />}
          {typeof children === 'function' ? children(renderProps) : children}
        </>
      )}
    </ListBoxItem>
  );
};
